import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LockIcon, EyeIcon, EyeOffIcon, CheckIcon } from 'lucide-react';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../firebase/firebaseConfig';
export const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isValidatingCode, setIsValidatingCode] = useState(true);
  const [oobCode, setOobCode] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    // Read oobCode from react-router location (works with both HashRouter and BrowserRouter)
    // Also fallback to window.location.search for direct URL access
    const routerParams = new URLSearchParams(location.search);
    const windowParams = new URLSearchParams(window.location.search);
    const code = routerParams.get('oobCode') || windowParams.get('oobCode');
    if (!code) {
      setError('Invalid or expired reset link. Please request a new password reset.');
      setIsValidatingCode(false);
      return;
    }
    setOobCode(code);
    verifyPasswordResetCode(auth, code).then(() => {
      setIsValidatingCode(false);
    }).catch(() => {
      setError('This password reset link is invalid or has expired. Please request a new one.');
      setIsValidatingCode(false);
    });
  }, [location.search]);
  const getPasswordStrength = (pw: string) => {
    if (!pw) return {
      level: 'none' as const,
      score: 0,
      label: '',
      checks: {
        minLength: false,
        hasUpper: false,
        hasLower: false,
        hasNumber: false,
        hasSpecial: false
      }
    };
    let score = 0;
    const checks = {
      minLength: pw.length >= 8,
      hasUpper: /[A-Z]/.test(pw),
      hasLower: /[a-z]/.test(pw),
      hasNumber: /[0-9]/.test(pw),
      hasSpecial: /[^A-Za-z0-9]/.test(pw)
    };
    if (checks.minLength) score++;
    if (checks.hasUpper) score++;
    if (checks.hasLower) score++;
    if (checks.hasNumber) score++;
    if (checks.hasSpecial) score++;
    let level: 'none' | 'weak' | 'medium' | 'strong' = 'weak';
    let label = 'Weak password';
    if (score >= 5) {
      level = 'strong';
      label = 'Strong password';
    } else if (score >= 3) {
      level = 'medium';
      label = 'Medium password';
    }
    return {
      level,
      score,
      label,
      checks
    };
  };
  const passwordStrength = getPasswordStrength(password);
  const passwordsMatch = confirmPw.length > 0 && password === confirmPw;
  const isPasswordValid = passwordStrength.level === 'strong' || passwordStrength.level === 'medium';
  const isFormValid = isPasswordValid && passwordsMatch && oobCode !== null;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || !oobCode) return;
    setIsSubmitting(true);
    setError('');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess(true);
    } catch (err: any) {
      if (err.code === 'auth/expired-action-code') {
        setError('This password reset link has expired. Please request a new one.');
      } else if (err.code === 'auth/invalid-action-code') {
        setError('This password reset link is invalid. Please request a new one.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use a stronger password.');
      } else {
        setError('An error occurred while resetting your password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isValidatingCode) {
    return <div className="min-h-screen w-full bg-gray-50 dark:bg-[#0A1C1A] flex items-center justify-center p-4" data-id="element-5795">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2DB489]" data-id="element-5796"></div>
      </div>;
  }
  const hasLinkError = error.includes('invalid') || error.includes('expired') || error.includes('missing');
  return <div className="min-h-screen w-full bg-gray-50 dark:bg-[#0A1C1A] flex items-center justify-center p-4" data-id="element-5797">
      <div className="bg-white dark:bg-[#162825] rounded-xl shadow-xl max-w-md w-full p-8" data-id="element-5798">
        <div className="text-center mb-8" data-id="element-5799">
          <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] mb-2" data-id="element-5800">
            DGStack
          </h1>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-5801">
            Reset Your Password
          </h2>
        </div>

        {success ? <div className="text-center" data-id="element-5802">
            <div className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded-md text-sm flex items-start text-left" data-id="element-5803">
              <CheckIcon className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-5804" />
              <p data-id="element-5805">
                Your password has been updated. You can now sign in with your
                new password.
              </p>
            </div>
            <button onClick={() => navigate('/')} className="w-full bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white px-6 py-3 rounded-lg text-base font-medium hover:shadow-lg transition-all" data-id="element-5806">
              Go to Home
            </button>
          </div> : <>
            {error && <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md text-sm" data-id="element-5807">
                {error}
              </div>}

            {!oobCode || hasLinkError ? <div className="text-center mt-6" data-id="element-5808">
                <button onClick={() => navigate('/')} className="w-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white px-6 py-3 rounded-lg text-base font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-all" data-id="element-5809">
                  Return to Home
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-5810">
                {/* New Password */}
                <div data-id="element-5811">
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5812">
                    New Password
                  </label>
                  <div className="relative" data-id="element-5813">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5814">
                      <LockIcon className="h-5 w-5 text-gray-400" data-id="element-5815" />
                    </div>
                    <input type={showPassword ? 'text' : 'password'} id="new-password" value={password} onChange={e => setPassword(e.target.value)} className="w-full pl-10 pr-10 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-white" placeholder="Create a new password" data-id="element-5816" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)} data-id="element-5817">
                      {showPassword ? <EyeOffIcon className="h-5 w-5 text-gray-400" data-id="element-5818" /> : <EyeIcon className="h-5 w-5 text-gray-400" data-id="element-5819" />}
                    </button>
                  </div>

                  {/* Password Strength Indicator */}
                  {password.length > 0 && <div className="mt-2.5 space-y-2" data-id="element-5820">
                      <div className="flex gap-1" data-id="element-5821">
                        {[1, 2, 3, 4, 5].map(segment => <div key={segment} className={`h-1.5 flex-1 rounded-full transition-colors duration-200 ${passwordStrength.score >= segment ? passwordStrength.level === 'strong' ? 'bg-emerald-500' : passwordStrength.level === 'medium' ? 'bg-amber-500' : 'bg-red-500' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-5822" />)}
                      </div>
                      <p className={`text-xs font-medium ${passwordStrength.level === 'strong' ? 'text-emerald-600 dark:text-emerald-400' : passwordStrength.level === 'medium' ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-5823">
                        {passwordStrength.label}
                      </p>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5" data-id="element-5824">
                        {[{
                  key: 'minLength',
                  label: '8+ characters'
                }, {
                  key: 'hasUpper',
                  label: 'Uppercase letter'
                }, {
                  key: 'hasLower',
                  label: 'Lowercase letter'
                }, {
                  key: 'hasNumber',
                  label: 'Number'
                }, {
                  key: 'hasSpecial',
                  label: 'Special character'
                }].map(({
                  key,
                  label
                }) => <span key={key} className={`text-xs flex items-center gap-1 ${(passwordStrength.checks as any)[key] ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-5825">
                            <CheckIcon className={`h-3 w-3 ${(passwordStrength.checks as any)[key] ? 'opacity-100' : 'opacity-30'}`} data-id="element-5826" />
                            {label}
                          </span>)}
                      </div>
                    </div>}
                </div>

                {/* Confirm Password */}
                <div data-id="element-5827">
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5828">
                    Confirm Password
                  </label>
                  <div className="relative" data-id="element-5829">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5830">
                      <LockIcon className="h-5 w-5 text-gray-400" data-id="element-5831" />
                    </div>
                    <input type={showConfirmPassword ? 'text' : 'password'} id="confirm-password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className={`w-full pl-10 pr-10 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:border-transparent dark:bg-[#0F1F1D] dark:text-white ${confirmPw.length === 0 ? 'border-gray-300 dark:border-gray-600 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0]' : passwordsMatch ? 'border-emerald-400 dark:border-emerald-500 focus:ring-emerald-500' : 'border-red-400 dark:border-red-500 focus:ring-red-500'}`} placeholder="Confirm your new password" data-id="element-5832" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowConfirmPassword(!showConfirmPassword)} data-id="element-5833">
                      {showConfirmPassword ? <EyeOffIcon className="h-5 w-5 text-gray-400" data-id="element-5834" /> : <EyeIcon className="h-5 w-5 text-gray-400" data-id="element-5835" />}
                    </button>
                  </div>
                  {confirmPw.length > 0 && !passwordsMatch && <p className="mt-1 text-xs text-red-600 dark:text-red-400" data-id="element-5836">
                      Passwords do not match
                    </p>}
                  {passwordsMatch && <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1" data-id="element-5837">
                      <CheckIcon className="h-3 w-3" data-id="element-5838" />
                      Passwords match
                    </p>}
                </div>

                {/* Submit Button */}
                <div className="pt-2" data-id="element-5839">
                  <button type="submit" disabled={isSubmitting || !isFormValid} className={`w-full bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white px-6 py-3 rounded-lg text-base font-medium hover:shadow-lg transition-all ${isSubmitting || !isFormValid ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-5840">
                    {isSubmitting ? <span className="flex items-center justify-center" data-id="element-5841">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-5842">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-5843"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-5844"></path>
                        </svg>
                        Resetting...
                      </span> : 'Reset Password'}
                  </button>
                </div>
              </form>}
          </>}
      </div>
    </div>;
};